import {
  API_PATHS,
  BRUSH_IDS,
  CONTRACT_VERSION,
  CURSOR_EVENT,
  MAX_POINTS,
  ROOM_ID,
  STICKER_IDS,
  STROKE_TTL_MS,
  ingestResponseSchema,
  metaTelemetryResponseSchema,
  recentStrokesResponseSchema,
  serverCursorPositionSchema,
  serverStrokeSnapshotSchema,
  type ClientStrokeSnapshot,
  type BrushId,
  type CursorPosition,
  type MetaTelemetryResponse,
  type Point,
  type ServerStrokeSnapshot,
  type ServerCursorPosition,
  type StickerId,
} from "@canvas/contracts";
import Pusher from "pusher-js";
import { buildBrushMarks } from "./brushes";
import { advanceTapCount } from "./easter-eggs";
import { toCanvasPoint } from "./pointer-point";
import { pointOpacity, updatePointTimeline, type PointTimeline } from "./stroke-timeline";
import { portalFaviconHref, portalIconSvg } from "./portal-icons";
import { StickerWindow } from "./sticker-window";
import "./fonts.css";
import "./brand-tokens.css";
import "./styles.css";

const SEND_INTERVAL_MS = 75;
// UI-only visible lifetime: each ink point fades this long after it arrives.
const STROKE_VISIBLE_MS = 3_500;
const STICKER_VISIBLE_MS = 5_000;
const STICKER_FADE_MS = 1_000;
// Activity census window: a tab counts as "drawing now" if any of its strokes
// landed inside this window. No backend needed — presence emerges from strokes.
const ACTIVE_WINDOW_MS = 10_000;
const CURSOR_SEND_INTERVAL_MS = 60;
const CURSOR_HEARTBEAT_MS = 1_500;
const CURSOR_STALE_MS = 5_000;
const CANVAS_WIDTH = 1_000;
const CANVAS_HEIGHT = 700;
// Public-directory assets are not content-hashed by Vite. Bump this whenever
// sticker artwork changes so long-lived edge/browser caches cannot retain an
// older opaque or low-resolution export.
const STICKER_ASSET_VERSION = "easter-egg-v3";
const SECRET_STICKER_ID: StickerId = "taylor";
const SECRET_STICKER_STORAGE_KEY = "canvas:taylor-sticker-unlocked:v1";
const SECRET_STICKER_WINDOW_TOKEN = "canvas-taylor-sticker-unlocked-v1";
const CREDIT_STORAGE_KEY = "canvas:laravel-credit-claimed:v2";
const CREDIT_WINDOW_TOKEN = "canvas-laravel-credit-claimed-v2";
const LARAVEL_PORTAL_URL = "https://multilang-canvas-demo-laravel.laravel.cloud";
const CREDIT_CLAIM_URL = import.meta.env.VITE_CANVAS_CREDIT_CLAIM_URL?.trim()
  || `${LARAVEL_PORTAL_URL}/api/v1/credit-claim`;

function stickerAsset(filename: string): string {
  return `/stickers/${filename}?v=${STICKER_ASSET_VERSION}`;
}

function element<T extends Element>(selector: string): T {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`Missing UI element: ${selector}`);
  return found;
}

const surface = element<SVGSVGElement>("#drawing-surface");
const strokeLayer = element<SVGGElement>("#stroke-layer");
const eraserLayer = element<SVGGElement>("#eraser-layer");
const canvasFrame = element<HTMLElement>(".canvas-frame");
const widthInput = element<HTMLInputElement>("#stroke-width");
const widthOutput = element<HTMLOutputElement>("#width-output");
const brushPicker = element<HTMLElement>("#brush-picker");
const serverTabsList = element<HTMLUListElement>("#server-tabs-list");
const serverPresenceList = element<HTMLUListElement>("#server-presence-list");
const stickerPicker = element<HTMLElement>("#sticker-picker");
const stickerSizeInput = element<HTMLInputElement>("#sticker-size");
const stickerSizeOutput = element<HTMLOutputElement>("#sticker-size-output");
const stickerLayer = element<HTMLElement>("#sticker-layer");
const remoteCursorLayer = element<HTMLElement>("#remote-cursor-layer");
const eraserPreview = element<HTMLElement>("#eraser-preview");
const adminPanel = element<HTMLElement>("#admin-panel");
const eraserToggle = element<HTMLButtonElement>("#eraser-toggle");
const eraserSizeInput = element<HTMLInputElement>("#eraser-size");
const eraserSizeOutput = element<HTMLOutputElement>("#eraser-size-output");
const clearCanvasButton = element<HTMLButtonElement>("#clear-canvas");
const brandWordmark = element<HTMLElement>(".brand-wordmark");
const confettiLayer = element<HTMLElement>("#confetti-layer");

interface StickerDefinition {
  id: StickerId;
  label: string;
  src: string;
  secret?: boolean;
}

const STICKERS: ReadonlyArray<StickerDefinition> = [
  { id: "laravel", label: "Laravel", src: stickerAsset("Default.svg") },
  { id: "facade", label: "Facade", src: stickerAsset("Facade.svg") },
  { id: "lambo", label: "Lambo", src: stickerAsset("Lambo.svg") },
  { id: "enter", label: "Enter key", src: stickerAsset("Sticker-cell-Enter.svg") },
  { id: "cosmic-salute", label: "Cosmic salute", src: stickerAsset("StickerCell-Cosmic-Salute.svg") },
  { id: "we-must-ship", label: "We must ship", src: stickerAsset("Variant2.svg") },
  { id: "coke-zero", label: "Coke Zero", src: stickerAsset("Zero.svg") },
  { id: "ship-keys", label: "Ship keys", src: stickerAsset("we-must-ship-key.svg") },
  { id: SECRET_STICKER_ID, label: "Taylor", src: stickerAsset("Taylor.png"), secret: true },
];
const stickersById = new Map(STICKERS.map((sticker) => [sticker.id, sticker]));

const STACKS = {
  express: { label: "Express", mark: "EX", lang: "Node.js" },
  laravel: { label: "Laravel", mark: "LA", lang: "PHP" },
  flask: { label: "Flask", mark: "FL", lang: "Python" },
  go: { label: "Go", mark: "GO", lang: "Go" },
  rails: { label: "Rails", mark: "RA", lang: "Ruby" },
} as const;

// Hosted portals offered by the server tabs.
const PORTALS: ReadonlyArray<{ id: keyof typeof STACKS; url: string }> = [
  { id: "laravel", url: LARAVEL_PORTAL_URL },
  { id: "express", url: "https://multilang-canvas-demo-express.laravel.cloud" },
  { id: "flask", url: "https://multilang-canvas-demo-flask.laravel.cloud" },
  { id: "go", url: "https://multilang-canvas-demo-go.laravel.cloud" },
  { id: "rails", url: "https://multilang-canvas-demo-rails.laravel.cloud" },
];

type StackId = keyof typeof STACKS;

type DrawableSnapshot = ClientStrokeSnapshot | ServerStrokeSnapshot;
interface DrawableRecord {
  snapshot: DrawableSnapshot;
  expiresAtMs: number;
}

const strokes = new Map<string, DrawableRecord>();
const stickerWindow = new StickerWindow();
let lastStickerPlacementAt = 0;
// First-seen (or last-updated) wall time per stroke; drives the UI-only fade.
const seenAtMs = new Map<string, number>();
// Persistent SVG elements so per-frame opacity updates interpolate instead of
// remounting (remounting is what made the fade look glitchy).
const drawableEls = new Map<string, SVGGraphicsElement | HTMLImageElement>();
// Each ink point keeps its own arrival time so a continuing stroke cannot
// refresh the already-drawn head. Timelines outlive their visible paths to
// prevent a sender from reviving old points with later revisions.
const pointTimelines = new Map<string, PointTimeline>();
// Last-seen wall time per drawing session, grouped by the adapter that
// accepted its stroke. This is a lightweight activity census, not presence.
const seenClientsByStack = new Map<StackId, Map<string, number>>(
  PORTALS.map((portal) => [portal.id, new Map<string, number>()]),
);
const initialPortal = PORTALS.find((portal) => portal.url === window.location.origin)?.id ?? null;
let currentStack: StackId | null = initialPortal;
let activeStroke: ClientStrokeSnapshot | null = null;
let activePointerId: number | null = null;
let selectedColor = "#00001e";
let selectedSize = Number(widthInput.value);
let selectedBrush: BrushId = "calligraphy";
let selectedSticker: StickerId | null = null;
let selectedStickerSize = Number(stickerSizeInput.value);
let selectedAdminTool: "eraser" | null = null;
let selectedEraserSize = Number(eraserSizeInput.value);
let lastCanvasClearAtMs = 0;
let localInkStarted = false;
let lastSendAt = 0;
let queuedSend: number | undefined;
let pusher: Pusher | null = null;
let reverbSignature = "";
let fadeRaf = 0;
let cursorBroadcastEnabled = false;
let localCursorPosition: CursorPosition | null = null;
let lastCursorSendAt = 0;
let cursorSendTimer: number | undefined;
let cursorSequence = 0;
let cursorRaf = 0;
let logoTapCount = 0;
let spaceTapCount = 0;
let creditStrokeIds: string[] = [];
let creditClaimPending = false;
let lastCreditClaimFingerprint = "";
let creditAcknowledgementQueue: Promise<void> = Promise.resolve();

interface RemoteCursor {
  element: HTMLElement;
  currentX: number;
  currentY: number;
  targetX: number;
  targetY: number;
  sequence: number;
  clientUpdatedAtMs: number;
  lastSeenAtMs: number;
}

const remoteCursors = new Map<string, RemoteCursor>();
const adminPowers = new URLSearchParams(window.location.search).get("adminpowers") === "true";

function hasPersistentUnlock(storageKey: string, windowToken: string): boolean {
  try {
    const locallyUnlocked = localStorage.getItem(storageKey) === "true";
    const bridgedUnlock = window.name.split("|").includes(windowToken);
    if (bridgedUnlock && !locallyUnlocked) localStorage.setItem(storageKey, "true");
    if (locallyUnlocked && !bridgedUnlock) {
      window.name = [window.name, windowToken].filter(Boolean).join("|");
    }
    return locallyUnlocked || bridgedUnlock;
  } catch {
    return window.name.split("|").includes(windowToken);
  }
}

function rememberPersistentUnlock(storageKey: string, windowToken: string): void {
  try {
    localStorage.setItem(storageKey, "true");
  } catch {
    // Private browsing can block storage; window.name still bridges this tab.
  }
  if (!window.name.split("|").includes(windowToken)) {
    window.name = [window.name, windowToken].filter(Boolean).join("|");
  }
}

let secretStickerUnlocked = hasPersistentUnlock(
  SECRET_STICKER_STORAGE_KEY,
  SECRET_STICKER_WINDOW_TOKEN,
);
let creditUnlocked = hasPersistentUnlock(CREDIT_STORAGE_KEY, CREDIT_WINDOW_TOKEN);

const storedClientId = sessionStorage.getItem("canvas-client-id");
const clientId = storedClientId ?? crypto.randomUUID();
sessionStorage.setItem("canvas-client-id", clientId);

function snapshotTimestamp(snapshot: DrawableSnapshot): number {
  return Date.parse(
    snapshot.kind === "stroke.snapshot.server"
      ? snapshot.updatedAt
      : snapshot.clientUpdatedAt,
  );
}

function resetCanvasContent(): void {
  if (activePointerId !== null && surface.hasPointerCapture(activePointerId)) {
    surface.releasePointerCapture(activePointerId);
  }
  activeStroke = null;
  activePointerId = null;
  if (queuedSend !== undefined) {
    window.clearTimeout(queuedSend);
    queuedSend = undefined;
  }
  strokes.clear();
  stickerWindow.clear();
  seenAtMs.clear();
  pointTimelines.clear();
  drawableEls.forEach((drawable) => drawable.remove());
  drawableEls.clear();
  localInkStarted = false;
  delete canvasFrame.dataset.drawn;
  canvasFrame.dataset.ink = "false";
}

function applyCanvasClear(clearedAtMs: number): void {
  if (clearedAtMs <= lastCanvasClearAtMs) return;
  lastCanvasClearAtMs = clearedAtMs;
  resetCanvasContent();
}

function recordPresence(snapshot: DrawableSnapshot, observedAtMs = Date.now()): void {
  if (snapshot.style.tool) return;
  const stack = snapshot.kind === "stroke.snapshot.server"
    ? snapshot.origin.adapter
    : currentStack;
  if (!stack) return;
  seenClientsByStack.get(stack)?.set(snapshot.clientId, observedAtMs);
}

function updateStrokeTimeline(snapshot: DrawableSnapshot, observedAtMs: number): void {
  if (snapshot.style.sticker || snapshot.style.tool === "clear") return;
  const firstObservedAtMs = snapshot.kind === "stroke.snapshot.server"
    ? observedAtMs - Math.max(0, Date.parse(snapshot.updatedAt) - Date.parse(snapshot.createdAt))
    : observedAtMs;
  pointTimelines.set(
    snapshot.strokeId,
    updatePointTimeline(
      pointTimelines.get(snapshot.strokeId),
      snapshot.points.length,
      observedAtMs,
      firstObservedAtMs,
      snapshot.kind === "stroke.snapshot.client" ? SEND_INTERVAL_MS : 0,
    ),
  );
}

function reconcile(snapshot: DrawableSnapshot, observedAtMs = Date.now()): void {
  const updatedAtMs = snapshotTimestamp(snapshot);
  if (snapshot.style.tool === "clear") {
    applyCanvasClear(updatedAtMs);
    return;
  }
  if (updatedAtMs <= lastCanvasClearAtMs) return;
  const current = strokes.get(snapshot.strokeId);
  if (current) {
    if (snapshot.revision < current.snapshot.revision) return;
    if (
      snapshot.revision === current.snapshot.revision &&
      snapshot.kind === current.snapshot.kind &&
      snapshotTimestamp(snapshot) <= snapshotTimestamp(current.snapshot)
    ) {
      return;
    }
    if (
      snapshot.revision === current.snapshot.revision &&
      snapshot.kind === "stroke.snapshot.client" &&
      current.snapshot.kind === "stroke.snapshot.server"
    ) {
      return;
    }
  }

  const removedStickers = stickerWindow.accept(snapshot, Date.now());
  for (const id of removedStickers) {
    strokes.delete(id);
    seenAtMs.delete(id);
    removeDrawable(id);
  }
  if (removedStickers.includes(snapshot.strokeId)) return;

  strokes.set(snapshot.strokeId, {
    snapshot,
    expiresAtMs:
      snapshot.kind === "stroke.snapshot.server"
        ? Date.parse(snapshot.expiresAt)
        : Date.now() + STROKE_TTL_MS,
  });
  seenAtMs.set(snapshot.strokeId, observedAtMs);
  updateStrokeTimeline(snapshot, observedAtMs);
  recordPresence(snapshot, observedAtMs);
  kickFadeLoop();
  updatePresence();
}

function updatePresence(): void {
  const now = Date.now();
  for (const portal of PORTALS) {
    const seenClients = seenClientsByStack.get(portal.id);
    if (!seenClients) continue;
    for (const [clientId, at] of seenClients) {
      if (now - at > ACTIVE_WINDOW_MS) seenClients.delete(clientId);
    }

    const count = seenClients.size;
    const item = serverPresenceList.querySelector<HTMLElement>(`[data-presence-portal="${portal.id}"]`);
    const badge = item?.querySelector<HTMLElement>(".server-presence-count");
    if (badge && badge.textContent !== String(count)) badge.textContent = String(count);
    item?.setAttribute("data-active", String(count > 0));
    item?.setAttribute(
      "aria-label",
      `${STACKS[portal.id].label}: ${count} ${count === 1 ? "person" : "people"} drawing now`,
    );
  }
}

function visibleLifetime(snapshot: DrawableSnapshot): number {
  return snapshot.style.sticker ? STICKER_VISIBLE_MS : STROKE_VISIBLE_MS;
}

function removeDrawable(strokeId: string): void {
  drawableEls.get(strokeId)?.remove();
  drawableEls.delete(strokeId);
}

function renderSticker(strokeId: string, snapshot: DrawableSnapshot): HTMLImageElement {
  const sticker = snapshot.style.sticker
    ? stickersById.get(snapshot.style.sticker)
    : undefined;
  let image = drawableEls.get(strokeId);
  if (!(image instanceof HTMLImageElement)) {
    removeDrawable(strokeId);
    image = document.createElement("img");
    image.className = "canvas-sticker";
    image.dataset.strokeId = strokeId;
    image.alt = "";
    image.decoding = "async";
    image.draggable = false;
    stickerLayer.append(image);
    drawableEls.set(strokeId, image);
  }

  const [x, y] = snapshot.points[0];
  image.src = sticker?.src ?? "";
  image.style.left = `${(x / 1_000) * 100}%`;
  image.style.top = `${(y / 700) * 100}%`;
  const scale = Math.max(0.25, Math.min(1, snapshot.style.size / 32));
  image.style.width = `${13.2 * scale}%`;
  image.style.height = `${17.15 * scale}%`;
  return image;
}

function renderStroke(
  strokeId: string,
  snapshot: DrawableSnapshot,
  points: readonly Point[],
  timeline: PointTimeline | undefined,
  now: number,
  lifetime: number,
): { group: SVGGElement; visibleSegments: number } {
  const isEraser = snapshot.style.tool === "eraser";
  const renderKind = isEraser ? "eraser" : "ink";
  let group = drawableEls.get(strokeId);
  if (group?.tagName.toLowerCase() !== "g" || group.dataset.renderKind !== renderKind) {
    removeDrawable(strokeId);
    group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    group.dataset.strokeId = strokeId;
    group.dataset.renderKind = renderKind;
    (isEraser ? eraserLayer : strokeLayer).append(group);
    drawableEls.set(strokeId, group);
  }
  const strokeGroup = group as SVGGElement;
  const brush = snapshot.style.brush ?? "round";
  const geometryKey = `${snapshot.revision}:${points.length}:${snapshot.style.color}:${snapshot.style.size}:${brush}:${snapshot.style.tool ?? "ink"}`;
  if (strokeGroup.dataset.geometry !== geometryKey) {
    strokeGroup.replaceChildren();
    strokeGroup.dataset.geometry = geometryKey;
    const fadeGroups = new Map<number, SVGGElement>();
    for (const mark of buildBrushMarks({
      brush,
      color: snapshot.style.color,
      isEraser,
      points,
      size: snapshot.style.size,
      strokeId,
    })) {
      const fadeBucket = timeline?.seenAtMs[mark.pointIndex] ?? mark.pointIndex;
      let fadeGroup = fadeGroups.get(fadeBucket);
      if (!fadeGroup) {
        fadeGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
        fadeGroup.dataset.pointIndex = String(mark.pointIndex);
        fadeGroup.dataset.baseOpacity = brush === "marker" && !isEraser ? "0.58" : "1";
        fadeGroups.set(fadeBucket, fadeGroup);
        strokeGroup.append(fadeGroup);
      }
      const brushMark = document.createElementNS("http://www.w3.org/2000/svg", mark.element);
      for (const [name, value] of Object.entries(mark.attributes)) brushMark.setAttribute(name, value);
      fadeGroup.append(brushMark);
    }
  }

  let visibleSegments = 0;
  for (const segment of strokeGroup.children) {
    const pointIndex = Number((segment as SVGElement).dataset.pointIndex ?? 0);
    const temporalOpacity = pointOpacity(timeline, pointIndex, now, lifetime);
    const baseOpacity = Number((segment as SVGElement).dataset.baseOpacity ?? 1);
    const opacity = isEraser && temporalOpacity > 0 ? 1 : temporalOpacity * baseOpacity;
    segment.setAttribute("opacity", String(opacity));
    if (opacity > 0) visibleSegments += 1;
  }
  return { group: strokeGroup, visibleSegments };
}

function render(): void {
  const now = Date.now();
  stickerWindow.prune(now);

  for (const [strokeId, timeline] of pointTimelines) {
    if (now - timeline.lastObservedAtMs > STROKE_TTL_MS) pointTimelines.delete(strokeId);
  }

  for (const [strokeId, record] of strokes) {
    const remaining = record.expiresAtMs - now;
    const ageMs = now - (seenAtMs.get(strokeId) ?? now);
    const lifetime = visibleLifetime(record.snapshot);
    if (remaining <= 0) {
      strokes.delete(strokeId);
      seenAtMs.delete(strokeId);
      pointTimelines.delete(strokeId);
      removeDrawable(strokeId);
      continue;
    }

    const snapshot = record.snapshot;
    if (snapshot.style.sticker) {
      if (ageMs > lifetime) {
        strokes.delete(strokeId);
        seenAtMs.delete(strokeId);
        removeDrawable(strokeId);
        continue;
      }
      const sticker = renderSticker(strokeId, snapshot);
      const fadeAgeMs = Math.max(0, ageMs - (lifetime - STICKER_FADE_MS));
      sticker.style.opacity = String(1 - fadeAgeMs / STICKER_FADE_MS);
      continue;
    }

    const timeline = pointTimelines.get(strokeId);
    const rendered = renderStroke(strokeId, snapshot, snapshot.points, timeline, now, lifetime);
    if (rendered.visibleSegments === 0) {
      strokes.delete(strokeId);
      seenAtMs.delete(strokeId);
      removeDrawable(strokeId);
      continue;
    }
  }

  canvasFrame.dataset.ink = strokes.size > 0 ? "true" : "false";
}

// Re-render every animation frame while content is visible so old ink points
// peel off the head and the remaining tail fades smoothly.
function kickFadeLoop(): void {
  if (fadeRaf !== 0) return;
  const tick = (): void => {
    if (strokes.size === 0) {
      fadeRaf = 0;
      return;
    }
    render();
    fadeRaf = requestAnimationFrame(tick);
  };
  fadeRaf = requestAnimationFrame(tick);
}

function canvasPoint(clientX: number, clientY: number, pressure = 0.5): Point {
  const bounds = surface.getBoundingClientRect();
  return toCanvasPoint({
    clientX,
    clientY,
    pressure,
    left: bounds.left,
    top: bounds.top,
    width: bounds.width,
    height: bounds.height,
  });
}

function pointerPoint(event: PointerEvent): Point {
  return canvasPoint(event.clientX, event.clientY, event.pressure);
}

function cursorPosition(event: PointerEvent): CursorPosition {
  const [x, y] = canvasPoint(event.clientX, event.clientY);
  return { x, y };
}

function cursorMarker(cursor: ServerCursorPosition): HTMLElement {
  const marker = document.createElement("div");
  marker.className = "remote-cursor";
  marker.dataset.adapter = cursor.origin.adapter;
  marker.innerHTML = portalIconSvg(cursor.origin.adapter, 16);
  return marker;
}

function removeRemoteCursor(remoteClientId: string): void {
  remoteCursors.get(remoteClientId)?.element.remove();
  remoteCursors.delete(remoteClientId);
}

function reconcileCursor(cursor: ServerCursorPosition): void {
  if (cursor.clientId === clientId) return;
  const incomingAt = Date.parse(cursor.clientUpdatedAt);
  const current = remoteCursors.get(cursor.clientId);
  if (
    current &&
    (incomingAt < current.clientUpdatedAtMs ||
      (incomingAt === current.clientUpdatedAtMs && cursor.sequence <= current.sequence))
  ) {
    return;
  }
  if (!cursor.position) {
    removeRemoteCursor(cursor.clientId);
    return;
  }

  const record = current ?? {
    element: cursorMarker(cursor),
    currentX: cursor.position.x,
    currentY: cursor.position.y,
    targetX: cursor.position.x,
    targetY: cursor.position.y,
    sequence: cursor.sequence,
    clientUpdatedAtMs: incomingAt,
    lastSeenAtMs: Date.now(),
  };
  if (!current) {
    remoteCursorLayer.append(record.element);
    remoteCursors.set(cursor.clientId, record);
  } else if (record.element.dataset.adapter !== cursor.origin.adapter) {
    record.element.dataset.adapter = cursor.origin.adapter;
    record.element.innerHTML = portalIconSvg(cursor.origin.adapter, 16);
  }
  record.targetX = cursor.position.x;
  record.targetY = cursor.position.y;
  record.sequence = cursor.sequence;
  record.clientUpdatedAtMs = incomingAt;
  record.lastSeenAtMs = Date.now();
  kickCursorLoop();
}

function kickCursorLoop(): void {
  if (cursorRaf !== 0) return;
  let previous = performance.now();
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const tick = (now: number): void => {
    const elapsed = Math.min(64, now - previous);
    previous = now;
    const alpha = reducedMotion ? 1 : 1 - Math.exp(-elapsed / 70);
    const wallNow = Date.now();
    for (const [remoteClientId, cursor] of remoteCursors) {
      if (wallNow - cursor.lastSeenAtMs > CURSOR_STALE_MS) {
        removeRemoteCursor(remoteClientId);
        continue;
      }
      cursor.currentX += (cursor.targetX - cursor.currentX) * alpha;
      cursor.currentY += (cursor.targetY - cursor.currentY) * alpha;
      cursor.element.style.left = `${(cursor.currentX / CANVAS_WIDTH) * 100}%`;
      cursor.element.style.top = `${(cursor.currentY / CANVAS_HEIGHT) * 100}%`;
    }
    if (remoteCursors.size === 0) {
      cursorRaf = 0;
      return;
    }
    cursorRaf = requestAnimationFrame(tick);
  };
  cursorRaf = requestAnimationFrame(tick);
}

async function sendCursor(position: CursorPosition | null): Promise<void> {
  if (!cursorBroadcastEnabled) return;
  lastCursorSendAt = Date.now();
  cursorSequence = (cursorSequence + 1) % 1_000_000_001;
  try {
    const response = await fetch(API_PATHS.updateCursor, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        kind: "cursor.position.client",
        contractVersion: CONTRACT_VERSION,
        roomId: ROOM_ID,
        clientId,
        sequence: cursorSequence,
        position,
        clientUpdatedAt: new Date().toISOString(),
      }),
      keepalive: position === null,
    });
    if (!response.ok) throw new Error(`Cursor update returned ${response.status}`);
  } catch (error) {
    console.error(error);
  }
}

function queueCursor(position: CursorPosition | null, immediate = false): void {
  localCursorPosition = position;
  if (!cursorBroadcastEnabled) return;
  if (cursorSendTimer !== undefined) {
    window.clearTimeout(cursorSendTimer);
    cursorSendTimer = undefined;
  }
  const wait = immediate
    ? 0
    : Math.max(0, CURSOR_SEND_INTERVAL_MS - (Date.now() - lastCursorSendAt));
  if (wait === 0) {
    void sendCursor(position);
    return;
  }
  cursorSendTimer = window.setTimeout(() => {
    cursorSendTimer = undefined;
    void sendCursor(localCursorPosition);
  }, wait);
}

function cloneSnapshot(snapshot: ClientStrokeSnapshot): ClientStrokeSnapshot {
  return {
    ...snapshot,
    style: { ...snapshot.style },
    points: snapshot.points.map((point) => [...point] as Point),
  };
}

async function sendSnapshot(snapshot: ClientStrokeSnapshot): Promise<ServerStrokeSnapshot | null> {
  lastSendAt = Date.now();
  try {
    const response = await fetch(API_PATHS.ingestStroke, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(snapshot),
    });
    const payload: unknown = await response.json();
    if (!response.ok) throw new Error(`Ingest returned ${response.status}`);
    const parsed = ingestResponseSchema.parse(payload);
    reconcile(parsed.snapshot);
    return parsed.snapshot;
  } catch (error) {
    console.error(error);
    return null;
  }
}

function queueActiveSnapshot(): void {
  if (!activeStroke || queuedSend !== undefined) return;
  const wait = Math.max(0, SEND_INTERVAL_MS - (Date.now() - lastSendAt));
  if (wait === 0) {
    void sendSnapshot(cloneSnapshot(activeStroke));
    return;
  }
  queuedSend = window.setTimeout(() => {
    queuedSend = undefined;
    if (activeStroke) void sendSnapshot(cloneSnapshot(activeStroke));
  }, wait);
}

function setSelectedSticker(sticker: StickerId | null): void {
  selectedSticker = sticker;
  if (sticker) selectedAdminTool = null;
  updateToolState();
}

function setAdminTool(tool: "eraser" | null): void {
  selectedAdminTool = tool;
  if (tool) selectedSticker = null;
  updateToolState();
}

function updateToolState(): void {
  canvasFrame.dataset.tool = selectedAdminTool ?? (selectedSticker ? "sticker" : "draw");
  document.querySelectorAll<HTMLElement>(".swatch").forEach((swatch) => {
    swatch.classList.toggle(
      "selected",
      !selectedSticker && !selectedAdminTool && swatch.dataset.color === selectedColor,
    );
  });
  for (const button of brushPicker.querySelectorAll<HTMLButtonElement>(".brush-option")) {
    const selected = !selectedSticker && !selectedAdminTool && button.dataset.brush === selectedBrush;
    button.classList.toggle("selected", selected);
    button.setAttribute("aria-pressed", String(selected));
  }
  for (const button of stickerPicker.querySelectorAll<HTMLButtonElement>(".sticker-button")) {
    button.setAttribute("aria-pressed", String(button.dataset.sticker === selectedSticker));
  }
  eraserToggle.setAttribute("aria-pressed", String(selectedAdminTool === "eraser"));
  if (selectedAdminTool !== "eraser") eraserPreview.hidden = true;
}

function placeSticker(sticker: StickerId, point: Point): void {
  // Preserve click order even when several placements share a millisecond.
  lastStickerPlacementAt = Math.max(Date.now(), lastStickerPlacementAt + 1);
  const snapshot: ClientStrokeSnapshot = {
    kind: "stroke.snapshot.client",
    contractVersion: CONTRACT_VERSION,
    roomId: ROOM_ID,
    strokeId: crypto.randomUUID(),
    clientId,
    revision: 0,
    completed: true,
    clientUpdatedAt: new Date(lastStickerPlacementAt).toISOString(),
    style: { color: "#00001e", size: selectedStickerSize, sticker },
    points: [point],
  };
  localInkStarted = true;
  canvasFrame.dataset.drawn = "true";
  reconcile(snapshot);
  void sendSnapshot(cloneSnapshot(snapshot));
}

function clearEveryoneCanvas(): void {
  const snapshot: ClientStrokeSnapshot = {
    kind: "stroke.snapshot.client",
    contractVersion: CONTRACT_VERSION,
    roomId: ROOM_ID,
    strokeId: crypto.randomUUID(),
    clientId,
    revision: 0,
    completed: true,
    clientUpdatedAt: new Date().toISOString(),
    style: { color: "#00001e", size: 1, tool: "clear" },
    points: [[0, 0, 1]],
  };
  // Clear immediately, then let the server-stamped response establish the
  // canonical cutoff shared by every client. Client clocks are not trusted.
  resetCanvasContent();
  void sendSnapshot(cloneSnapshot(snapshot));
}

function updateEraserPreview(event: PointerEvent): void {
  if (selectedAdminTool !== "eraser") {
    eraserPreview.hidden = true;
    return;
  }
  const bounds = surface.getBoundingClientRect();
  const inside = event.clientX >= bounds.left && event.clientX <= bounds.right
    && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
  eraserPreview.hidden = !inside;
  if (!inside) return;
  eraserPreview.style.left = `${event.clientX - bounds.left}px`;
  eraserPreview.style.top = `${event.clientY - bounds.top}px`;
  eraserPreview.style.setProperty("--eraser-preview-width", `${(selectedEraserSize / CANVAS_WIDTH) * bounds.width}px`);
  eraserPreview.style.setProperty("--eraser-preview-height", `${(selectedEraserSize / CANVAS_HEIGHT) * bounds.height}px`);
}

function addStickerButton(sticker: StickerDefinition): HTMLButtonElement {
  const existing = stickerPicker.querySelector<HTMLButtonElement>(`[data-sticker="${sticker.id}"]`);
  if (existing) return existing;
  const button = document.createElement("button");
  button.type = "button";
  button.className = "sticker-button";
  button.dataset.sticker = sticker.id;
  button.draggable = true;
  button.setAttribute("aria-label", `${sticker.label} sticker`);
  button.setAttribute("aria-pressed", "false");
  const image = document.createElement("img");
  image.src = sticker.src;
  image.alt = "";
  image.decoding = "async";
  image.loading = "lazy";
  image.draggable = false;
  button.append(image);
  stickerPicker.append(button);
  return button;
}

function initStickerPicker(): void {
  for (const sticker of STICKERS) {
    if (sticker.secret && !secretStickerUnlocked) continue;
    addStickerButton(sticker);
  }

  stickerPicker.addEventListener("click", (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>(".sticker-button");
    const sticker = button?.dataset.sticker;
    if (!sticker || !STICKER_IDS.includes(sticker as StickerId)) return;
    setSelectedSticker(selectedSticker === sticker ? null : sticker as StickerId);
  });

  stickerPicker.addEventListener("dragstart", (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>(".sticker-button");
    const sticker = button?.dataset.sticker;
    if (!sticker || !STICKER_IDS.includes(sticker as StickerId) || !event.dataTransfer) return;
    setSelectedSticker(sticker as StickerId);
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("application/x-canvas-sticker", sticker);
    event.dataTransfer.setData("text/plain", sticker);
    button.classList.add("dragging");
  });

  stickerPicker.addEventListener("dragend", (event) => {
    (event.target as Element).closest(".sticker-button")?.classList.remove("dragging");
  });
}

function unlockSecretSticker(): void {
  if (secretStickerUnlocked) return;
  secretStickerUnlocked = true;
  rememberPersistentUnlock(SECRET_STICKER_STORAGE_KEY, SECRET_STICKER_WINDOW_TOKEN);
  const sticker = stickersById.get(SECRET_STICKER_ID);
  if (!sticker) return;
  const button = addStickerButton(sticker);
  button.classList.add("just-unlocked");
  button.addEventListener("animationend", () => button.classList.remove("just-unlocked"), { once: true });
  setSelectedSticker(SECRET_STICKER_ID);
  button.scrollIntoView({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    block: "nearest",
    inline: "nearest",
  });
}

function advanceTaylorTrigger(source: "logo" | "space"): void {
  if (secretStickerUnlocked) return;
  const count = source === "logo" ? logoTapCount : spaceTapCount;
  const result = advanceTapCount(count);
  if (source === "logo") logoTapCount = result.count;
  else spaceTapCount = result.count;
  if (result.unlocked) unlockSecretSticker();
}

function launchConfetti(): void {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const colors = ["#f53003", "#0057ff", "#71a8ff", "#ffc53d", "#29a383"];
  const release = (): void => {
    for (let index = 0; index < 24; index += 1) {
      const piece = document.createElement("i");
      piece.className = "confetti-piece";
      piece.style.setProperty("--confetti-left", `${Math.random() * 100}%`);
      piece.style.setProperty("--confetti-drift", `${Math.round(Math.random() * 160 - 80)}px`);
      piece.style.setProperty("--confetti-spin", `${Math.round(Math.random() * 720 + 360)}deg`);
      piece.style.setProperty("--confetti-delay", `${Math.random() * 300}ms`);
      piece.style.setProperty("--confetti-duration", `${Math.round(Math.random() * 900 + 1_900)}ms`);
      piece.style.setProperty("--confetti-color", colors[index % colors.length]);
      piece.addEventListener("animationend", () => piece.remove(), { once: true });
      confettiLayer.append(piece);
    }
  };
  release();
  const confettiInterval = window.setInterval(release, 600);
  window.setTimeout(() => window.clearInterval(confettiInterval), 9_000);
}

function showCreditToast(creditCode: string): void {
  if (document.querySelector("#credit-toast")) return;
  const toast = document.createElement("aside");
  toast.id = "credit-toast";
  toast.className = "credit-toast";
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");

  const copy = document.createElement("div");
  copy.className = "credit-toast-copy";
  const kicker = document.createElement("p");
  kicker.className = "credit-toast-kicker";
  kicker.textContent = "Laravel Cloud";
  const title = document.createElement("p");
  title.className = "credit-toast-title";
  title.textContent = "$20 credit unlocked";
  const description = document.createElement("p");
  description.className = "credit-toast-description";
  description.textContent = "Your Laravel Cloud credit code is:";
  const code = document.createElement("code");
  code.className = "credit-toast-code";
  code.textContent = creditCode;
  copy.append(kicker, title, description, code);

  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.className = "credit-toast-dismiss";
  dismiss.textContent = "Got it";
  dismiss.addEventListener("click", () => toast.remove());
  toast.append(copy, dismiss);
  document.body.append(toast);
}

function unlockCredit(creditCode: string): void {
  // Reveal the reward every time the sequence is drawn, even on a device that
  // has already claimed it. The code is never stored locally; each reveal is a
  // fresh, server-validated claim.
  if (!creditUnlocked) {
    creditUnlocked = true;
    rememberPersistentUnlock(CREDIT_STORAGE_KEY, CREDIT_WINDOW_TOKEN);
  }
  showCreditToast(creditCode);
  launchConfetti();
}

async function claimCredit(strokeIds: string[]): Promise<void> {
  if (creditClaimPending) return;
  const fingerprint = strokeIds.join("|");
  if (fingerprint === lastCreditClaimFingerprint) return;
  lastCreditClaimFingerprint = fingerprint;
  creditClaimPending = true;
  try {
    const response = await fetch(CREDIT_CLAIM_URL, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ clientId, strokeIds }),
    });
    if ([409, 422, 429].includes(response.status)) return;
    if (!response.ok) throw new Error(`Credit claim returned ${response.status}`);
    const payload: unknown = await response.json();
    if (
      typeof payload !== "object"
      || payload === null
      || !("kind" in payload)
      || payload.kind !== "canvas.credit-claim"
      || !("creditCode" in payload)
      || typeof payload.creditCode !== "string"
      || payload.creditCode.length < 1
      || payload.creditCode.length > 200
    ) {
      throw new Error("Credit claim returned an invalid response");
    }
    unlockCredit(payload.creditCode);
  } catch (error) {
    console.error(error);
  } finally {
    creditClaimPending = false;
    const latestStrokeIds = [...creditStrokeIds];
    if (
      latestStrokeIds.length === 7
      && latestStrokeIds.join("|") !== lastCreditClaimFingerprint
    ) {
      void claimCredit(latestStrokeIds);
    }
  }
}

function recordCreditStroke(snapshot: ServerStrokeSnapshot): void {
  creditStrokeIds = [...creditStrokeIds.slice(-6), snapshot.strokeId];
  if (creditStrokeIds.length === 7) void claimCredit([...creditStrokeIds]);
}

function initEasterEggs(): void {
  console.info(
    "👀 Canvas has two developer Easter eggs. Seven repeats unlock one. For the other, the key lies in the width.",
  );
  brandWordmark.addEventListener("click", (event) => {
    event.preventDefault();
    advanceTaylorTrigger("logo");
  });
  window.addEventListener("keydown", (event) => {
    const target = event.target as HTMLElement | null;
    const isEditing = target?.matches("input, textarea, select, [contenteditable='true']") ?? false;
    if (event.code !== "Space" || event.repeat || isEditing || secretStickerUnlocked) return;
    event.preventDefault();
    advanceTaylorTrigger("space");
  });
  brandWordmark.addEventListener("dragstart", (event) => event.preventDefault());
}

surface.addEventListener("pointerdown", (event) => {
  if (activeStroke) return;
  event.preventDefault();
  if (selectedSticker) {
    placeSticker(selectedSticker, pointerPoint(event));
    return;
  }
  surface.setPointerCapture(event.pointerId);
  activePointerId = event.pointerId;
  if (!localInkStarted) {
    localInkStarted = true;
    canvasFrame.dataset.drawn = "true";
  }
  const now = new Date().toISOString();
  activeStroke = {
    kind: "stroke.snapshot.client",
    contractVersion: CONTRACT_VERSION,
    roomId: ROOM_ID,
    strokeId: crypto.randomUUID(),
    clientId,
    revision: 0,
    completed: false,
    clientUpdatedAt: now,
    style: selectedAdminTool === "eraser"
      ? { color: "#00001e", size: selectedEraserSize, tool: "eraser" }
      : { color: selectedColor, size: selectedSize, brush: selectedBrush },
    points: [pointerPoint(event)],
  };
  reconcile(activeStroke);
  queueActiveSnapshot();
});

surface.addEventListener("pointermove", (event) => {
  queueCursor(cursorPosition(event));
  updateEraserPreview(event);
  if (!activeStroke || event.pointerId !== activePointerId) return;
  event.preventDefault();
  const incoming = event.getCoalescedEvents?.() ?? [event];
  const available = MAX_POINTS - activeStroke.points.length;
  if (available <= 0) {
    return;
  }
  const points = incoming.slice(-available).map(pointerPoint);
  if (points.length === 0) return;
  activeStroke = {
    ...activeStroke,
    revision: activeStroke.revision + 1,
    clientUpdatedAt: new Date().toISOString(),
    points: [...activeStroke.points, ...points],
  };
  reconcile(activeStroke);
  queueActiveSnapshot();
});

surface.addEventListener("pointerenter", (event) => {
  queueCursor(cursorPosition(event), true);
  updateEraserPreview(event);
});

surface.addEventListener("pointerleave", () => {
  eraserPreview.hidden = true;
  if (activePointerId === null) queueCursor(null, true);
});

function completeStroke(event: PointerEvent): void {
  if (!activeStroke || event.pointerId !== activePointerId) return;
  event.preventDefault();
  if (queuedSend !== undefined) {
    window.clearTimeout(queuedSend);
    queuedSend = undefined;
  }
  activeStroke = {
    ...activeStroke,
    revision: activeStroke.revision + 1,
    completed: true,
    clientUpdatedAt: new Date().toISOString(),
  };
  reconcile(activeStroke);
  const completedStroke = cloneSnapshot(activeStroke);
  const acknowledgement = sendSnapshot(completedStroke);
  if (event.type === "pointerup" && completedStroke.style.tool !== "eraser") {
    creditAcknowledgementQueue = creditAcknowledgementQueue.then(async () => {
      const serverSnapshot = await acknowledgement;
      if (serverSnapshot) recordCreditStroke(serverSnapshot);
    });
  }
  activeStroke = null;
  activePointerId = null;
  if (surface.hasPointerCapture(event.pointerId)) surface.releasePointerCapture(event.pointerId);
}

surface.addEventListener("pointerup", completeStroke);
surface.addEventListener("pointercancel", completeStroke);
surface.addEventListener("dragover", (event) => {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
  canvasFrame.dataset.dropTarget = "true";
});
surface.addEventListener("dragleave", () => {
  delete canvasFrame.dataset.dropTarget;
});
surface.addEventListener("drop", (event) => {
  event.preventDefault();
  delete canvasFrame.dataset.dropTarget;
  const sticker = event.dataTransfer?.getData("application/x-canvas-sticker")
    || event.dataTransfer?.getData("text/plain");
  if (!sticker || !STICKER_IDS.includes(sticker as StickerId)) return;
  setSelectedSticker(sticker as StickerId);
  placeSticker(sticker as StickerId, canvasPoint(event.clientX, event.clientY));
});

element("#color-swatches").addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>(".swatch");
  if (!button?.dataset.color) return;
  setSelectedSticker(null);
  setAdminTool(null);
  selectedColor = button.dataset.color;
  document.querySelectorAll(".swatch").forEach((swatch) => swatch.classList.remove("selected"));
  button.classList.add("selected");
});

brushPicker.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>(".brush-option");
  const brush = button?.dataset.brush;
  if (!brush || !BRUSH_IDS.includes(brush as BrushId)) return;
  selectedBrush = brush as BrushId;
  setSelectedSticker(null);
  setAdminTool(null);
  updateToolState();
});

widthInput.addEventListener("input", () => {
  setSelectedSticker(null);
  setAdminTool(null);
  selectedSize = Number(widthInput.value);
  widthOutput.value = String(selectedSize);
});

stickerSizeInput.addEventListener("input", () => {
  selectedStickerSize = Number(stickerSizeInput.value);
  stickerSizeOutput.value = `${Math.round((selectedStickerSize / 32) * 100)}%`;
});

eraserToggle.addEventListener("click", () => {
  setAdminTool(selectedAdminTool === "eraser" ? null : "eraser");
});

eraserSizeInput.addEventListener("input", () => {
  selectedEraserSize = Number(eraserSizeInput.value);
  eraserSizeOutput.value = String(selectedEraserSize);
});

clearCanvasButton.addEventListener("click", clearEveryoneCanvas);

function markCurrentPortal(adapter: string): void {
  for (const item of serverTabsList.querySelectorAll<HTMLAnchorElement>("a[data-portal]")) {
    const current = item.dataset.portal === adapter;
    if (current) item.setAttribute("aria-current", "page");
    else item.removeAttribute("aria-current");
  }
}

function frameworkMark(adapter: StackId, size: number, className: string): HTMLElement {
  const mark = document.createElement("span");
  mark.className = `framework-mark ${className}`;
  mark.dataset.adapter = adapter;
  mark.setAttribute("aria-hidden", "true");
  mark.innerHTML = portalIconSvg(adapter, size);
  return mark;
}

function initServerNavigation(): void {
  for (const portal of PORTALS) {
    const item = document.createElement("li");
    const link = document.createElement("a");
    link.href = adminPowers ? `${portal.url}?adminpowers=true` : portal.url;
    link.dataset.portal = portal.id;
    link.append(frameworkMark(portal.id, 15, "server-tab-mark"));
    const label = document.createElement("span");
    label.textContent = STACKS[portal.id].label;
    link.append(label);
    item.append(link);
    serverTabsList.append(item);

    const presenceItem = document.createElement("li");
    presenceItem.className = "server-presence-item";
    presenceItem.dataset.presencePortal = portal.id;
    const name = document.createElement("span");
    name.className = "server-presence-name";
    name.textContent = STACKS[portal.id].label;
    const count = document.createElement("span");
    count.className = "server-presence-count";
    count.textContent = "0";
    count.setAttribute("aria-hidden", "true");
    presenceItem.append(frameworkMark(portal.id, 13, "server-presence-mark"), name, count);
    serverPresenceList.append(presenceItem);
  }
  markCurrentPortal(initialPortal ?? "");
  updatePresence();
}

function updateAdapterIdentity(meta: MetaTelemetryResponse): void {
  const adapter = meta.adapter.adapter.toLowerCase();
  const presentation = STACKS[adapter as keyof typeof STACKS] ?? {
    label: meta.adapter.adapter,
    mark: meta.adapter.adapter.slice(0, 2).toUpperCase(),
  };

  document.body.dataset.adapter = adapter;
  document.title = `${presentation.label} · Canvas`;
  let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!favicon) {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    favicon.type = "image/svg+xml";
    document.head.append(favicon);
  }
  favicon.href = portalFaviconHref(adapter);
  if (adapter in STACKS) currentStack = adapter as StackId;
  markCurrentPortal(adapter);
}

function connectReverb(meta: MetaTelemetryResponse): void {
  const config = meta.reverb;
  const signature = JSON.stringify(config);
  if (signature === reverbSignature) return;
  reverbSignature = signature;
  pusher?.disconnect();
  pusher = null;
  cursorBroadcastEnabled = config.enabled;

  if (!config.enabled) {
    return;
  }

  pusher = new Pusher(config.appKey, {
    cluster: config.cluster ?? "mt1",
    wsHost: config.host,
    wsPort: config.wsPort,
    wssPort: config.wssPort,
    forceTLS: config.forceTLS,
    enabledTransports: ["ws", "wss"],
    disableStats: true,
  });
  const channel = pusher.subscribe(config.channel);
  channel.bind(config.event, (payload: unknown) => {
    const parsed = serverStrokeSnapshotSchema.safeParse(payload);
    if (!parsed.success) return;
    reconcile(parsed.data);
  });
  channel.bind(CURSOR_EVENT, (payload: unknown) => {
    const parsed = serverCursorPositionSchema.safeParse(payload);
    if (!parsed.success) return;
    reconcileCursor(parsed.data);
  });
}

async function loadRecent(): Promise<void> {
  try {
    const response = await fetch(API_PATHS.recentStrokes);
    const payload = recentStrokesResponseSchema.parse(await response.json());
    // Live-only replay: with a store shared across portals, boot-time
    // history would otherwise replay other servers' last ~30s of strokes on
    // every portal switch. Instead, backdate each snapshot by its true server
    // age — strokes still inside the visible window fade in mid-ramp exactly
    // as if we'd watched them start, and anything older stays invisible.
    // Live strokes keep flowing in via Reverb (and our own draws) from here.
    const serverNow = Date.parse(payload.serverTime);
    const chronologicalSnapshots = [...payload.snapshots].sort((left, right) =>
      Date.parse(left.updatedAt) - Date.parse(right.updatedAt)
      || left.strokeId.localeCompare(right.strokeId));
    chronologicalSnapshots.forEach((snapshot) => {
      const trueAgeMs = serverNow - Date.parse(snapshot.updatedAt);
      const observedAtMs = Date.now() - Math.max(0, trueAgeMs);
      if (trueAgeMs <= ACTIVE_WINDOW_MS) recordPresence(snapshot, observedAtMs);
      // A clear marker must survive longer than the visual ink window so a
      // pre-clear sticker cannot reappear during boot-time replay.
      if (snapshot.style.tool !== "clear" && trueAgeMs > visibleLifetime(snapshot)) return;
      reconcile(snapshot, observedAtMs);
    });
    updatePresence();
  } catch (error) {
    console.error(error);
  }
}

async function refreshMeta(): Promise<void> {
  try {
    const response = await fetch(API_PATHS.meta, { cache: "no-store" });
    const meta = metaTelemetryResponseSchema.parse(await response.json());
    updateAdapterIdentity(meta);
    connectReverb(meta);
  } catch (error) {
    console.error(error);
  }
}

setInterval(() => {
  render();
  updatePresence();
}, 250);
setInterval(() => void refreshMeta(), 5_000);
setInterval(() => {
  if (localCursorPosition) void sendCursor(localCursorPosition);
}, CURSOR_HEARTBEAT_MS);

window.addEventListener("blur", () => queueCursor(null, true));
window.addEventListener("pagehide", () => queueCursor(null, true));
document.addEventListener("visibilitychange", () => {
  if (document.hidden) queueCursor(null, true);
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  if (selectedSticker) setSelectedSticker(null);
  if (selectedAdminTool) setAdminTool(null);
});

function initAdminPowers(): void {
  if (!adminPowers) return;
  adminPanel.hidden = false;
  document.body.dataset.adminPowers = "true";
  updateToolState();
}

initStickerPicker();
initEasterEggs();
initServerNavigation();
initAdminPowers();
void Promise.all([loadRecent(), refreshMeta()]);
